// src/fleet-stats/components/MostChangedSection.jsx
import React, { useState } from "react";
import { TrendingDown, TrendingUp, Activity } from "lucide-react";
import { C } from "../constants.js";
import { DataTable } from "./SharedUI.jsx";
import { fmtNum, fmtChangePct } from "../utils/format.js";

// Tab definitions with full plain-English descriptions
const TABS = [
    {
        key: "drops",
        label: "Biggest drops",
        icon: TrendingDown,
        color: "#ff5f5f",
        description: "Robots whose average daily distance in the last 7 days dropped significantly compared to their own activity in the 3 weeks before that. Weekends are excluded from both windows to avoid false alerts from normal warehouse patterns. A drop here means the robot is driving less than it normally does — not less than other robots.",
        emptyMessage: "No significant drops detected in the last 7 days.",
        type: "drops",
    },
    {
        key: "gains",
        label: "Biggest gains",
        icon: TrendingUp,
        color: "#7bff89",
        description: "Robots whose average daily distance in the last 7 days increased significantly compared to their own activity in the 3 weeks before that. Weekends excluded. A gain here could reflect a new shift pattern, expanded route, or a robot returning to service after downtime.",
        emptyMessage: "No significant gains detected in the last 7 days.",
        type: "gains",
    },
    {
        key: "highUptime",
        label: "High uptime, low drive",
        icon: Activity,
        color: "#f5a623",
        description: "Robots that were powered on for significant periods in the last 7 days but spent less than 10% of that time actually driving. This could indicate a robot waiting for mission assignments, blocked by an obstacle, or running but not deployed. Uptime and driving hours shown for the last 7 days.",
        emptyMessage: "No robots with high uptime and low driving detected.",
        type: "highUptime",
    },
];

function ChangedTable({ rows, type }) {
    if (rows.length === 0) return null;

    const isHighUptime = type === "highUptime";

    const columns = isHighUptime
        ? [
            { key: "client", label: "Client" },
            { key: "siteName", label: "Site" },
            {
                key: "robotName",
                label: "Robot",
                render: (r) => (
                    <div>
                        <div className="font-medium">{r.robotName}</div>
                        <div className="text-xs" style={{ color: C.grey2 }}>{r.robotId}</div>
                    </div>
                ),
            },
            {
                key: "utilization",
                label: "% time driving",
                align: "right",
                render: (r) => (
                    <span style={{ color: "#f5a623", fontWeight: 600 }}>
                        {Math.round((r.utilization || 0) * 100)}%
                    </span>
                ),
            },
            {
                key: "recentUptimeH",
                label: "Uptime last 7d (h)",
                align: "right",
                render: (r) => fmtNum(r.recentUptimeH, 1),
            },
            {
                key: "recentDrivingH",
                label: "Driving last 7d (h)",
                align: "right",
                render: (r) => fmtNum(r.recentDrivingH, 1),
            },
        ]
        : [
            { key: "client", label: "Client" },
            { key: "siteName", label: "Site" },
            {
                key: "robotName",
                label: "Robot",
                render: (r) => (
                    <div>
                        <div className="font-medium">{r.robotName}</div>
                        <div className="text-xs" style={{ color: C.grey2 }}>{r.robotId}</div>
                    </div>
                ),
            },
            {
                key: "recentKm",
                label: "Last 7d avg km/day",
                align: "right",
                render: (r) => fmtNum(r.recentKm, 2),
            },
            {
                key: "baselineKm",
                label: "Prior 3wk avg km/day",
                align: "right",
                render: (r) => (
                    <span style={{ color: C.grey2 }}>{fmtNum(r.baselineKm, 2)}</span>
                ),
            },
            {
                key: "changePct",
                label: "Change",
                align: "right",
                render: (r) => (
                    <span style={{ fontWeight: 700, color: r.changePct < 0 ? "#ff5f5f" : C.neonGreen }}>
                        {fmtChangePct(r.changePct)}
                    </span>
                ),
            },
        ];

    return (
        <DataTable
            columns={columns}
            rows={rows.map((r) => ({ ...r, _key: r.robotId }))}
            maxVisibleRows={8}
        />
    );
}

export function MostChangedSection({ mostChanged }) {
    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("drops");

    const total =
        mostChanged.biggestDrops.length +
        mostChanged.biggestGains.length +
        mostChanged.highUptimeLowDrive.length;

    const tabData = {
        drops:     mostChanged.biggestDrops,
        gains:     mostChanged.biggestGains,
        highUptime: mostChanged.highUptimeLowDrive,
    };

    const activeTabConfig = TABS.find((t) => t.key === activeTab);

    return (
        <div className="mb-8">
            <button
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-2 text-sm font-semibold mb-1 transition-colors"
                style={{ color: C.grey1 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = C.neonGreen)}
                onMouseLeave={(e) => (e.currentTarget.style.color = C.grey1)}
            >
                <TrendingDown size={16} />
                {open ? "Hide" : "Show"} movers — last 7d vs own 3-week baseline ({total})
            </button>

            {/* Always-visible description */}
            <div className="text-xs mb-3" style={{ color: C.grey2 }}>
                Compares each robot's last 7 days against its own prior 3-week baseline. Weekends excluded. Each robot is compared only to itself — not to other robots or fleet averages.
            </div>

            {open && (
                <div>
                    {/* Tabs */}
                    <div className="flex flex-wrap gap-2 mb-3">
                        {TABS.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.key;
                            const count = tabData[tab.key]?.length || 0;
                            return (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors"
                                    style={{
                                        backgroundColor: isActive ? "rgba(255,255,255,0.06)" : "transparent",
                                        color: isActive ? tab.color : C.grey2,
                                        border: `1px solid ${isActive ? tab.color + "55" : "rgba(255,255,255,0.08)"}`,
                                    }}
                                >
                                    <Icon size={14} style={{ color: isActive ? tab.color : C.grey2 }} />
                                    {tab.label}
                                    {count > 0 && (
                                        <span
                                            className="ml-1 px-1.5 py-0.5 rounded-full text-xs"
                                            style={{
                                                backgroundColor: isActive ? `${tab.color}22` : "rgba(255,255,255,0.05)",
                                                color: isActive ? tab.color : C.grey2,
                                            }}
                                        >
                                            {count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Active tab description */}
                    {activeTabConfig && (
                        <div
                            className="text-xs rounded-lg px-3 py-2.5 mb-3 leading-relaxed"
                            style={{
                                backgroundColor: "rgba(255,255,255,0.02)",
                                border: "1px solid rgba(255,255,255,0.06)",
                                color: C.grey1,
                            }}
                        >
                            {activeTabConfig.description}
                        </div>
                    )}

                    {/* Table or empty state */}
                    {tabData[activeTab]?.length === 0 ? (
                        <div className="text-sm py-2 px-1" style={{ color: C.grey2 }}>
                            {activeTabConfig?.emptyMessage || "None detected."}
                        </div>
                    ) : (
                        <ChangedTable rows={tabData[activeTab]} type={activeTab} />
                    )}
                </div>
            )}
        </div>
    );
}